import type { HttpClient } from 'effect/http';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import type { Context, Crypto, ManagedRuntime } from 'effect';
import {
  createContext,
  createElement,
  type ReactNode,
  useContext,
} from 'react';

// The app's `ManagedRuntime` for shared code that runs an Effect from a React
// event rather than through an atom. Each app builds its own (start has
// `clientRuntime`, browser-ext and mobile their own) and mounts
// `<AppRuntimeProvider runtime>` once at the root.

type ApiClientCtx = Context.Service.Identifier<typeof ApiClient>;

/** Everything a shared program may ask of the app runtime. Named so callers
 * that hand an Effect to `runtime.runFork` can state the requirement. */
export type AppServices = ApiClientCtx | HttpClient.HttpClient | Crypto.Crypto;

// Minimum the shared layer needs. Each app's actual runtime may provide more
// (Logger, Analytics, etc.) — covariant in R, so wider runtimes assign
// to this type. HttpClient is here for the doc-pack ranged reads (and any
// other shared program that talks to plain HTTP, not the typed ApiClient) —
// every app runtime supplies FetchHttpClient.layer.
export type AppRuntime = ManagedRuntime.ManagedRuntime<AppServices, never>;

const AppRuntimeContext = createContext<AppRuntime | null>(null);

export function AppRuntimeProvider({
  runtime,
  children,
}: {
  runtime: AppRuntime;
  children: ReactNode;
}) {
  return createElement(
    AppRuntimeContext.Provider,
    { value: runtime },
    children,
  );
}

export const useAppRuntime = (): AppRuntime => {
  const runtime = useContext(AppRuntimeContext);
  if (!runtime) {
    throw new Error(
      'AppRuntimeProvider missing from React tree — mount <AppRuntimeProvider runtime={...}> at the app root.',
    );
  }
  return runtime;
};
